import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import "../Buscar/Buscar.css";
import "./Agenda.css";
import WithAuthorization from "../../../../app/src/Utils/WithAuthorization";
import { turnOffLoadingActionCreator } from "../../../../app/src/actions/commonActions";
import { getTranslatedName } from "../../../../app/src/Utils/utils";
import {
  AGENDA_CARDS_PER_DAY,
  AGENDA_DAYS_LIMIT,
  AGENDA_DAY_BATCH,
  AGENDA_DAY_BATCH_MOBILE,
  isNodeNotFoundError,
  searchAgendaDay,
  searchAgendaDays,
} from "../../../../app/src/services/agendaService";
import { resolveAgendaDay } from "../../../../app/src/Utils/agendaDateUtils";

import { useAgendaUrlState } from "./state/agendaUrlState";
import { useAgendaFilterNodes } from "./state/useAgendaFilterNodes";
import { useAgendaFormat } from "./state/useAgendaFormat";
import { AGENDA_COLUMNS, AGENDA_COLUMNS_MOBILE, useElementHeight, useFixedToolbar, useHeaderHeight, useNowTick } from "./state/useAgendaLayout";
import { useIsMobile } from "../Buscar/state/useIsMobile";

import FilterButton from "../Buscar/components/FilterButton";
import FilterChips from "../Buscar/components/FilterChips";
import FilterModal from "../Buscar/components/FilterModal";
import EmptyState from "../Buscar/components/EmptyState";
import DateStrip from "./components/DateStrip";
import DayBlock, { AgendaGrid, DayBlockSkeleton, DayHeader } from "./components/DayBlock";
import { AgendaEmpty, BackToDates, NoTodayNotice } from "./components/AgendaMessages";

// Página "Agenda" (OTT-2011, Fase 3). Página propia del front, como el buscador: ya no pasa por
// el renderizador de screens (screen/13). Especificación: "Agenda Especificacion" v2.3 (Claude
// Design) y docs/agenda-analisis.md.
//
// Dos vistas, según `dia` en la URL:
// - Lista por días (01/02/05/12): un bloque por día con UNA fila de tarjetas, lazy loading por
//   páginas de 7 días y scroll-spy sobre la tira de fechas.
// - Vista de un día (03/13, "Ver más"): rejilla completa con lazy loading en lotes de 8/15.

const HIDE_SCROLLBAR_CLASS = "ag-hide-scrollbar";
// Tiempo en el que el scroll-spy no toca la casilla marcada tras un salto programático (el scroll
// suave pasa por encima de otros días).
const SPY_LOCK_MS = 900;
const SKELETON_BLOCKS = 2;

// Último día en el que estuvo el usuario (casilla pulsada, "Ver más" o scroll-spy): al volver a la
// Agenda en la misma sesión (atrás desde una ficha o desde el menú) la lista se coloca en ese día.
// Supuesto: solo el día, sin los filtros (esos viven en la URL), y solo durante la sesión del
// navegador. Si el almacenamiento no está disponible, la Agenda abre arriba, como siempre.
const LAST_DAY_KEY = "agenda.lastDay";
const readLastDay = () => {
  try {
    return window.sessionStorage.getItem(LAST_DAY_KEY);
  } catch (e) {
    return null;
  }
};
const saveLastDay = (day) => {
  try {
    window.sessionStorage.setItem(LAST_DAY_KEY, day);
  } catch (e) {
    // Sin almacenamiento (modo privado, cookies bloqueadas): no se recuerda el día.
  }
};

const INITIAL_LIST = { status: "idle", meta: null, days: [], page: 0, error: null };
const INITIAL_DAY = { status: "idle", day: null, items: [], page: 0, filterCount: 0, error: null };

const AgendaPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const format = useAgendaFormat();
  const interfazId = useSelector((state) => state.commonState.interfazId);

  const {
    roots,
    childrenByParent,
    loadChildren,
    resolveIds,
    resolveSlugs,
    hasFilters,
    loading: nodesLoading,
    allNodeId,
  } = useAgendaFilterNodes(interfazId);
  const { state: url, setSportSlugs, openDay, closeDay, clearFilters, ignoreDay, replaceSportSlugs } = useAgendaUrlState();

  const isMobile = useIsMobile(768);
  const dayBatch = isMobile ? AGENDA_DAY_BATCH_MOBILE : AGENDA_DAY_BATCH;
  const columns = isMobile ? AGENDA_COLUMNS_MOBILE : AGENDA_COLUMNS;
  const nowMs = useNowTick();
  const headerHeight = useHeaderHeight();
  const pageRef = useRef(null);
  const toolbarAnchorRef = useRef(null);
  const toolbarRef = useRef(null);
  const toolbarHeight = useElementHeight(toolbarRef);
  const toolbarBox = useFixedToolbar(toolbarAnchorRef, pageRef, headerHeight);
  const contentRef = useRef(null);

  const [list, setList] = useState(INITIAL_LIST);
  const [dayView, setDayView] = useState(INITIAL_DAY);
  const [loadingMoreDays, setLoadingMoreDays] = useState(false);
  const [loadingMoreItems, setLoadingMoreItems] = useState(false);
  const [activeDay, setActiveDay] = useState(null);
  const [pendingScrollDay, setPendingScrollDay] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  const listStateRef = useRef(list);
  listStateRef.current = list;
  const listControllerRef = useRef(null);
  const dayControllerRef = useRef(null);
  const spyLockRef = useRef(0);
  const listSentinelRef = useRef(null);
  const daySentinelRef = useRef(null);
  const dayGridRef = useRef(null);
  const previousOpenDayRef = useRef(null);
  // Día a recuperar en la primera carga de la lista. Con `dia` en la URL manda la URL.
  const restoreDayRef = useRef(url.dia ? null : readLastDay());

  // Mismo motivo que en BuscarPage: el loader global arranca encendido en cada carga fresca y
  // esta página tiene sus propios skeletons.
  useEffect(() => {
    dispatch(turnOffLoadingActionCreator());
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sin barra de scroll vertical por debajo de 1024 px (pantallas 01/10): solo mientras la Agenda
  // está montada, con una clase en <html> que Agenda.css aplica dentro de un media query.
  useEffect(() => {
    document.documentElement.classList.add(HIDE_SCROLLBAR_CLASS);
    return () => document.documentElement.classList.remove(HIDE_SCROLLBAR_CLASS);
  }, []);

  // Con `sport` en la URL hay que esperar al árbol de nodos para traducir slugs a ids; sin esa
  // espera la primera consulta saldría sin filtrar (mismo caso que en el buscador).
  const waitingForNodes = url.sportSlugs.length > 0 && nodesLoading;
  const nodeIds = useMemo(() => (waitingForNodes ? null : resolveIds(url.sportSlugs)), [waitingForNodes, resolveIds, url.sportSlugs]);
  const nodeIdsKey = nodeIds ? nodeIds.join(",") : null;
  const activeCount = nodeIds ? nodeIds.length : 0;

  const handleApiError = useCallback(
    (error, setter) => {
      if (error?.name === "AbortError") return;
      // 404 node-not-found: algún filtro de la URL ya no existe. El error no dice cuál, así que se
      // quitan todos los filtros y se relanza la consulta (contrato §19).
      if (isNodeNotFoundError(error)) {
        replaceSportSlugs([]);
        return;
      }
      console.error(error);
      setter((prev) => ({ ...prev, status: "error", error: { status: error?.status } }));
    },
    [replaceSportSlugs],
  );

  // ---------------------------------------------------------------------------------------------
  // Lista por días (respuesta D). También da meta.today/until/days a la tira en las dos vistas.
  // ---------------------------------------------------------------------------------------------
  useEffect(() => {
    if (nodeIdsKey === null) return undefined;
    listControllerRef.current?.abort();
    const controller = new AbortController();
    listControllerRef.current = controller;
    setList((prev) => ({ ...prev, status: "loading", error: null }));
    setLoadingMoreDays(false);

    searchAgendaDays(
      { nodes: nodeIds, page: 1, daysLimit: AGENDA_DAYS_LIMIT, limit: AGENDA_CARDS_PER_DAY, interfazId },
      { signal: controller.signal },
    )
      .then((res) => {
        if (controller.signal.aborted) return;
        setList({ status: "ready", meta: res.meta, days: res.days || [], page: 1, error: null });
      })
      .catch((error) => handleApiError(error, setList));

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interfazId, nodeIdsKey, retryTick]);

  const meta = list.meta;
  const listReady = list.status === "ready";
  const hasMoreDays = listReady && !!meta && list.days.length < (meta.days || []).length;

  /**
   * Pide las páginas de días siguientes: una (lazy loading) o, con `untilDay`, las que falten hasta
   * incluir ese día (salto desde la tira a un día aún no cargado, pantalla 02).
   * @returns {Promise<boolean>} si el día pedido está cargado al terminar
   */
  const loadMoreDays = useCallback(
    async (untilDay = null) => {
      const current = listStateRef.current;
      const totalDays = current.meta?.days?.length || 0;
      const isLoaded = (days) => !untilDay || days.some((d) => d.date === untilDay);
      if (current.status !== "ready" || isLoaded(current.days)) return isLoaded(current.days);
      if (current.days.length >= totalDays) return false;
      // Un día sin eventos (con los filtros vigentes) no va a llegar en ninguna página.
      if (untilDay && !current.meta.days.some((d) => d.date === untilDay)) return false;

      const controller = listControllerRef.current;
      setLoadingMoreDays(true);
      let { page, days } = current;
      try {
        do {
          // eslint-disable-next-line no-await-in-loop
          const res = await searchAgendaDays(
            { nodes: nodeIds, page: page + 1, daysLimit: AGENDA_DAYS_LIMIT, limit: AGENDA_CARDS_PER_DAY, interfazId },
            { signal: controller?.signal },
          );
          if (controller?.signal.aborted) return false;
          page += 1;
          days = [...days, ...(res.days || [])];
          const snapshot = { page, days };
          setList((prev) => ({ ...prev, ...snapshot }));
          if (!(res.days || []).length) break;
        } while (!isLoaded(days) && days.length < totalDays);
      } catch (error) {
        if (error?.name !== "AbortError") console.error(error);
        return false;
      } finally {
        if (!controller?.signal.aborted) setLoadingMoreDays(false);
      }
      return isLoaded(days);
    },
    [nodeIds, interfazId],
  );

  // ---------------------------------------------------------------------------------------------
  // Vista de un día (respuesta A)
  // ---------------------------------------------------------------------------------------------
  // `dia` solo vale si está en el horizonte y tiene eventos con los filtros vigentes; hasta que no
  // llega la respuesta D no se puede saber.
  const openDayValue = listReady ? resolveAgendaDay(url.dia, meta) : null;
  const inDayView = !!url.dia && (!listReady || !!openDayValue);

  useEffect(() => {
    if (url.dia && listReady && !openDayValue) ignoreDay();
  }, [url.dia, listReady, openDayValue, ignoreDay]);

  useEffect(() => {
    if (!openDayValue || nodeIdsKey === null) return undefined;
    dayControllerRef.current?.abort();
    const controller = new AbortController();
    dayControllerRef.current = controller;
    setDayView({ ...INITIAL_DAY, status: "loading", day: openDayValue });
    setLoadingMoreItems(false);

    searchAgendaDay({ day: openDayValue, nodes: nodeIds, page: 1, limit: dayBatch, interfazId }, { signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        setDayView({
          status: "ready",
          day: openDayValue,
          items: res.data || [],
          page: 1,
          filterCount: res.meta?.filter_count ?? 0,
          error: null,
        });
      })
      .catch((error) => handleApiError(error, setDayView));

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDayValue, nodeIdsKey, dayBatch, interfazId, retryTick]);

  const hasMoreItems = dayView.status === "ready" && dayView.items.length < dayView.filterCount;

  const loadMoreItems = useCallback(async () => {
    if (loadingMoreItems || !hasMoreItems) return;
    const controller = dayControllerRef.current;
    setLoadingMoreItems(true);
    try {
      const res = await searchAgendaDay(
        { day: dayView.day, nodes: nodeIds, page: dayView.page + 1, limit: dayBatch, interfazId },
        { signal: controller?.signal },
      );
      if (controller?.signal.aborted) return;
      setDayView((prev) => ({
        ...prev,
        items: [...prev.items, ...(res.data || [])],
        page: prev.page + 1,
        filterCount: res.meta?.filter_count ?? prev.filterCount,
      }));
    } catch (error) {
      if (error?.name !== "AbortError") console.error(error);
    } finally {
      if (!controller?.signal.aborted) setLoadingMoreItems(false);
    }
  }, [loadingMoreItems, hasMoreItems, dayView.day, dayView.page, nodeIds, dayBatch, interfazId]);

  // ---------------------------------------------------------------------------------------------
  // Scroll: salto a un día, scroll-spy y lazy loading
  // ---------------------------------------------------------------------------------------------
  const scrollOffset = headerHeight + toolbarHeight + 8;

  const scrollToDay = useCallback(
    (day, behavior = "smooth") => {
      const node = document.getElementById(`ag-day-${day}`);
      if (!node) return false;
      spyLockRef.current = Date.now() + SPY_LOCK_MS;
      const top = node.getBoundingClientRect().top + window.scrollY - scrollOffset;
      window.scrollTo({ top: Math.max(0, top), behavior });
      return true;
    },
    [scrollOffset],
  );

  // El scroll a un día que acaba de cargarse (o al volver de la vista de día) se hace después de
  // pintarlo, cuando ya existe su bloque.
  useLayoutEffect(() => {
    if (!pendingScrollDay || inDayView) return;
    if (scrollToDay(pendingScrollDay, "auto")) setPendingScrollDay(null);
  }, [pendingScrollDay, inDayView, list.days, scrollToDay]);

  // Casilla marcada al llegar la lista: el primer día con eventos (hoy si hay; si no, el
  // siguiente, 01-B). Al cambiar los filtros se vuelve arriba (supuesto: el contenido es otro).
  // Al volver a la Agenda, la primera carga se coloca en el último día visitado si todavía tiene
  // eventos con los filtros vigentes (si ya pasó o se quedó sin eventos, se abre arriba).
  useEffect(() => {
    if (!listReady || inDayView) return;
    if (pendingScrollDay) return;
    const restoreDay = restoreDayRef.current;
    restoreDayRef.current = null;
    const firstDay = meta?.days?.[0]?.date;
    if (restoreDay && restoreDay !== firstDay && (meta?.days || []).some((d) => d.date === restoreDay)) {
      setActiveDay(restoreDay);
      spyLockRef.current = Date.now() + SPY_LOCK_MS * 3;
      setPendingScrollDay(restoreDay);
      loadMoreDays(restoreDay);
      return;
    }
    setActiveDay(firstDay ?? meta?.today ?? null);
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  useEffect(() => {
    if (activeDay) saveLastDay(activeDay);
  }, [activeDay]);

  // Entrar en la vista de día: arriba del todo. Salir (Volver o atrás): scroll al bloque del día
  // que se había abierto, cargándolo si hace falta.
  useEffect(() => {
    const previous = previousOpenDayRef.current;
    if (openDayValue && openDayValue !== previous) {
      setActiveDay(openDayValue);
      window.scrollTo(0, 0);
    } else if (!url.dia && previous) {
      // Si el día ya no tiene eventos (p.ej. se cambiaron los filtros dentro de la vista de día),
      // no hay bloque al que volver: se queda la lista tal cual.
      const stillListed = (listStateRef.current.meta?.days || []).some((d) => d.date === previous);
      if (stillListed) {
        setActiveDay(previous);
        setPendingScrollDay(previous);
        loadMoreDays(previous);
      }
    }
    previousOpenDayRef.current = openDayValue || (url.dia ? previous : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDayValue, url.dia]);

  // Scroll-spy (pantalla 02): la casilla marcada es la del día cuyo bloque está arriba del todo.
  useEffect(() => {
    if (inDayView || !listReady) return undefined;
    let frame = null;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        if (Date.now() < spyLockRef.current || !contentRef.current) return;
        const sections = contentRef.current.querySelectorAll("section[data-day]");
        if (!sections.length) return;
        const limit = scrollOffset + 20;
        let current = sections[0].dataset.day;
        sections.forEach((section) => {
          if (section.getBoundingClientRect().top <= limit) current = section.dataset.day;
        });
        setActiveDay((prev) => (prev === current ? prev : current));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [inDayView, listReady, scrollOffset]);

  // Lazy loading de la lista: al acercarse al final, los 7 días siguientes.
  useEffect(() => {
    if (inDayView || !hasMoreDays || loadingMoreDays) return undefined;
    const node = listSentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver((entries) => entries[0]?.isIntersecting && loadMoreDays(), { rootMargin: "600px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [inDayView, hasMoreDays, loadingMoreDays, loadMoreDays, list.days.length]);

  // Lazy loading de la vista de día: sentinel 2 filas antes del final (pantalla 03).
  useEffect(() => {
    if (!inDayView || !hasMoreItems || loadingMoreItems) return undefined;
    const node = daySentinelRef.current;
    if (!node) return undefined;
    const firstCard = dayGridRef.current?.querySelector(".ag-grid > *");
    const rowHeight = firstCard ? firstCard.offsetHeight + 24 : 300;
    const observer = new IntersectionObserver((entries) => entries[0]?.isIntersecting && loadMoreItems(), {
      rootMargin: `${rowHeight * 2}px`,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [inDayView, hasMoreItems, loadingMoreItems, loadMoreItems, dayView.items.length]);

  // ---------------------------------------------------------------------------------------------
  // Acciones
  // ---------------------------------------------------------------------------------------------
  const onSelectDay = (day) => {
    setActiveDay(day);
    if (inDayView) {
      // Dentro de la vista de día, otra fecha cambia la vista a ese día (no vuelve a la lista).
      if (day !== openDayValue) openDay(day);
      return;
    }
    // En la lista la fecha no filtra: marca y salta. Si el día aún no está cargado, se piden las
    // páginas que falten (con skeletons) y después se hace el scroll.
    if (!scrollToDay(day)) {
      spyLockRef.current = Date.now() + SPY_LOCK_MS * 3;
      loadMoreDays(day).then((loaded) => loaded && setPendingScrollDay(day));
    }
  };

  const allNodes = useMemo(() => [...roots, ...Object.values(childrenByParent).flat()], [roots, childrenByParent]);
  const chips = (nodeIds || [])
    .map((id) => {
      const node = allNodes.find((n) => n.id === id);
      const slug = resolveSlugs([id])[0];
      return node && slug
        ? { key: slug, label: getTranslatedName(node), onRemove: () => setSportSlugs(url.sportSlugs.filter((s) => s !== slug)) }
        : null;
    })
    .filter(Boolean);

  // ---------------------------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------------------------
  const today = meta?.today || null;
  const listLoading = list.status === "loading" || list.status === "idle";
  const listError = list.status === "error" ? list.error : null;
  const isEmpty = listReady && (meta?.total === 0 || !(meta?.days || []).length);
  const showNoToday = listReady && !inDayView && !isEmpty && meta.days[0].date !== meta.today;
  const firstUpcoming = list.days[0]?.data?.[0];
  const noTodayText =
    showNoToday && firstUpcoming
      ? t("agenda.noTodayNext", { date: format.dayPhrase(list.days[0].date), time: format.time(firstUpcoming.start) })
      : "";

  const renderError = (error) =>
    error?.status === 400 ? (
      <AgendaEmpty filtered={activeCount > 0} onClear={clearFilters} />
    ) : (
      <EmptyState variant="error" onRetry={() => setRetryTick((n) => n + 1)} />
    );

  const renderList = () => {
    if (listError) return renderError(listError);
    if (listLoading) return Array.from({ length: SKELETON_BLOCKS }).map((_, i) => <DayBlockSkeleton key={i} columns={columns} />);
    if (isEmpty) return <AgendaEmpty filtered={activeCount > 0} onClear={clearFilters} />;
    return (
      <>
        {list.days.map((d) => (
          <DayBlock
            key={d.date}
            day={d.date}
            count={d.count}
            items={d.data || []}
            isToday={d.date === today}
            columns={columns}
            nowMs={nowMs}
            onMore={() => openDay(d.date)}
          />
        ))}
        {loadingMoreDays && Array.from({ length: SKELETON_BLOCKS }).map((_, i) => <DayBlockSkeleton key={`more-${i}`} columns={columns} />)}
        {hasMoreDays && <div ref={listSentinelRef} className="bq-sentinel" />}
      </>
    );
  };

  const renderDayView = () => {
    const day = openDayValue || url.dia;
    const dayMeta = (meta?.days || []).find((d) => d.date === day);
    const error = listError || (dayView.status === "error" ? dayView.error : null);
    const loading = !listReady || dayView.status !== "ready" || dayView.day !== openDayValue;
    return (
      <>
        <BackToDates onBack={closeDay} />
        {error ? (
          renderError(error)
        ) : (
          <section className="ag-day ag-day--open" data-day={day}>
            <DayHeader day={day} count={loading ? dayMeta?.count ?? 0 : dayView.filterCount} isToday={day === today} />
            <div ref={dayGridRef}>
              <AgendaGrid
                columns={columns}
                items={loading ? [] : dayView.items}
                nowMs={nowMs}
                skeletons={loading ? dayBatch : loadingMoreItems ? Math.min(dayBatch, dayView.filterCount - dayView.items.length) : 0}
                busy={loading || loadingMoreItems}
              />
            </div>
            {hasMoreItems && <div ref={daySentinelRef} className="bq-sentinel" />}
          </section>
        )}
      </>
    );
  };

  return (
    <div className="ag-page" ref={pageRef} style={{ "--ag-header-height": `${headerHeight}px` }}>
      {/* Sin título visible (ocupaba sitio sin aportar: el menú ya marca la Agenda); se mantiene el
          h1 para lectores de pantalla. */}
      <h1 className="bq-visually-hidden">{t("agenda.title")}</h1>

      <div ref={toolbarAnchorRef} style={toolbarBox.fixed ? { height: toolbarHeight } : undefined}>
      <div
        className={`ag-toolbar${toolbarBox.fixed ? " is-fixed" : ""}`}
        ref={toolbarRef}
        style={toolbarBox.fixed ? { left: toolbarBox.left, width: toolbarBox.width } : undefined}
      >
        <div className="ag-inner">
          <div className="ag-toolbar-row">
            {hasFilters && (
              <>
                <FilterButton
                  className="ag-filter-btn"
                  hasFilters={hasFilters}
                  activeCount={activeCount}
                  open={modalOpen}
                  onClick={() => setModalOpen(true)}
                />
                <span className="ag-toolbar-separator" aria-hidden="true" />
              </>
            )}
            <DateStrip today={today} until={meta?.until} metaDays={meta?.days} activeDay={activeDay} onSelect={onSelectDay} />
          </div>
          <FilterChips className="ag-chips" clearLabel={t("agenda.clear")} chips={chips} onClearAll={() => setSportSlugs([])} />
        </div>
      </div>
      </div>

      {/* El aviso va con la cabecera (centrado, ancho máximo); los resultados, a todo el ancho. */}
      {showNoToday && (
        <div className="ag-inner">
          <NoTodayNotice nextText={noTodayText} />
        </div>
      )}

      <div className="ag-inner ag-inner--full">
        <div className="ag-content" ref={contentRef}>
          {inDayView ? renderDayView() : renderList()}
        </div>
      </div>

      <FilterModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        roots={roots}
        childrenByParent={childrenByParent}
        loadChildren={loadChildren}
        selectedIds={nodeIds || []}
        allNodeId={allNodeId}
        onApply={(ids) => {
          setSportSlugs(resolveSlugs(ids));
          setModalOpen(false);
        }}
      />
    </div>
  );
};

export default WithAuthorization(AgendaPage);
